import React from 'react'
import { Grid, Card, CardMedia, Typography, Box, Button } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag'
import theme from 'theme'
import { Product } from 'hooks/useProduct'
import { PRODUCT_IMAGE_BASE_URL } from 'config'
import { useTranslation } from 'react-i18next'
import type { CabinetStyle } from 'constants/cabinetsData'

type Cabinet = CabinetStyle

type ProductsGridProps = {
  products: Cabinet[] | Product[]
  onOrderSample?: (color: string) => void
  type: 'Cabinets' | 'Hardware' | 'Richelieu'
}

const ProductCategoryGrid: React.FC<ProductsGridProps> = ({
  products,
  onOrderSample,
  type
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const handleNavigate = (product: Cabinet | Product) => {
    if (type === 'Cabinets') {
      const cabinet = product as Cabinet
      navigate(`/cabinets/${cabinet.color}`, {
        state: {
          imageUrl: cabinet.imageUrl,
          specification: cabinet.specification
        }
      })
    } else {
      const hardwareOrProduct = product as Product
      navigate(`/products/${hardwareOrProduct.id}`)
    }
  }

  return (
    <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
      {products.map((product, index) => (
        <Grid
          item
          key={index}
          xs={3}
          sx={{ display: 'flex', justifyContent: 'center' }}
        >
          <Card
            sx={{
              width: '100%',
              height: '350px',
              border: '1px solid grey',
              borderRadius: '10px',
              boxShadow: 'none',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              '&:hover': {
                border: `2px solid ${theme.palette.primary.main}`
              },
              '&:hover .hover-buttons': {
                display: 'flex',
                transform: type === 'Cabinets' ? 'translateY(-20px)' : 'none'
              },
              '&:hover .card-media': {
                transform: type === 'Cabinets' ? 'scale(0.7)' : 'scale(0.9)',
                width: type === 'Cabinets' ? '165px' : '200px',
                height: type === 'Cabinets' ? '200px' : '170px'
              },
              '&:hover .card-title': {
                transform:
                  type === 'Cabinets'
                    ? 'translateY(-30px)'
                    : 'translateY(-10px)'
              },
              ...(type === 'Cabinets' &&
              (product as Cabinet).isComingSoon
                ? {
                    cursor: 'default',
                    '&:hover': {
                      border: '1px solid #C8B99F'
                    },
                    '&:hover .hover-buttons': {
                      display: 'none'
                    },
                    '&:hover .card-media, &:hover .card-title': {
                      transform: 'none'
                    }
                  }
                : {})
            }}
          >
            {type === 'Cabinets' && (product as Cabinet).isComingSoon && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '14px',
                  right: '14px',
                  zIndex: 1,
                  px: '10px',
                  py: '5px',
                  borderRadius: '2px',
                  backgroundColor: '#F3EADF',
                  color: '#6E5435',
                  fontFamily: 'Inter',
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  lineHeight: 1.2
                }}
              >
                {t('ourProducts.comingSoon')}
              </Box>
            )}
            <CardMedia
              className='card-media'
              sx={{
                width: '210px',
                height: type === 'Cabinets' ? '255px' : '178px',
                transition: 'transform 0.3s'
              }}
              image={
                type === 'Cabinets'
                  ? (product as Cabinet).imageUrl
                  : `${PRODUCT_IMAGE_BASE_URL}${
                      (product as Product).imageIndex
                    }.png`
              }
              title={
                type === 'Cabinets'
                  ? (product as Cabinet).color
                  : (product as Product).sku
              }
            />
            <Typography
              className='card-title'
              variant='h4'
              sx={{
                fontWeight: 500,
                margin: '16px 0',
                transition: 'transform 0.3s'
              }}
            >
              {type === 'Cabinets'
                ? (product as Cabinet).color
                : (product as Product).sku}
            </Typography>
            {type === 'Cabinets' && (product as Cabinet).isComingSoon && (
              <Typography
                variant='body2'
                sx={{ color: 'text.secondary', mt: '-10px' }}
              >
                {t('ourProducts.availableSoon')}
              </Typography>
            )}
            <Box
              className='hover-buttons'
              sx={{
                display: 'none',
                flexDirection: 'column',
                alignItems: 'center',
                bottom: type === 'Cabinets' ? '20px' : 'none'
              }}
            >
              <Button
                sx={{
                  width: '222px',
                  height: '44px',
                  backgroundColor: 'primary.main',
                  color: '#FFFFFF',
                  fontFamily: 'Inter',
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: '14px',
                  textAlign: 'left',
                  padding: '10px 20px',
                  marginBottom: '10px',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: 'primary.main'
                  }
                }}
                onClick={() => handleNavigate(product)}
              >
                {t('ourProducts.shopThisStyle')}
              </Button>
              {type === 'Cabinets' && onOrderSample && (
                <Button
                  sx={{
                    width: '222px',
                    height: '44px',
                    color: 'primary.main',
                    fontFamily: 'Inter',
                    fontSize: '12px',
                    fontWeight: 500,
                    lineHeight: '14px',
                    textAlign: 'left',
                    padding: '10px 20px',
                    border: '1px solid',
                    backgroundColor: '#FFFFFF',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: '#FFFFFF'
                    }
                  }}
                  onClick={() => onOrderSample((product as Cabinet).color)}
                >
                  <ShoppingBagIcon sx={{ fontSize: '18px', mr: 0.5 }} />
                  {t('ourProducts.orderSample')}
                </Button>
              )}
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
  )
}

export default ProductCategoryGrid
